import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCreateVerification } from '../hooks/useCreateVerification';
import { VerificationFormFields, type VerificationFormData } from './VerificationFormFields';

interface VerificationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: string;
  experienceId: string;
}

function FormButtons({
  isLoading,
  onClose,
}: {
  isLoading: boolean;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Creating...' : 'Create Verification'}
      </Button>
    </DialogFooter>
  );
}

export function VerificationFormDialog({
  open,
  onOpenChange,
  candidateId,
  experienceId,
}: VerificationFormDialogProps): JSX.Element {
  const { createVerification, isLoading } = useCreateVerification();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<VerificationFormData>({
    defaultValues: { refereeEmail: '', refereeName: '', refereeJobTitle: '' },
  });

  const handleClose = (): void => onOpenChange(false);

  const onSubmit = handleSubmit((data) => {
    createVerification(
      {
        candidateId,
        experienceId,
        request: {
          refereeEmail: data.refereeEmail,
          refereeName: data.refereeName,
          refereeJobTitle: data.refereeJobTitle || null,
        },
      },
      {
        onSuccess: () => {
          reset();
          handleClose();
        },
      }
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Verification</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <VerificationFormFields register={register} errors={errors} />
          <FormButtons isLoading={isLoading} onClose={handleClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
